import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { AuthorizationConflictResponse } from '../../../domain/authorization';
import { type OtpRequest, VerificationRequest } from '../../../domain/qualification';
import { DataLoadState, ErrorType, initialDataLoadState } from '../../../store/reducers/common';

export type SessionStateBase = {
  verifying: boolean;
  authorized: boolean;
};

type SessionState = DataLoadState & SessionStateBase;

const initialState: SessionState = {
  ...initialDataLoadState,
  verifying: false,
  authorized: false,
};

const slice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    loadLocalStorageSession: (state) => ({
      ...state,
      loading: true,
    }),
    loadLocalStorageSessionSuccess: (state, { payload }: PayloadAction<SessionStateBase>) => ({
      ...state,
      ...payload,
      loading: false,
    }),
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    requestVerification: (state, _: PayloadAction<VerificationRequest>) => {
      state.error = null;
      state.loading = true;
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    requestOtp: (state, _: PayloadAction<OtpRequest>) => {
      state.error = null;
      state.loading = true;
    },
    resendVerification: (state) => {
      state.loading = true;
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    resendOtp: (state, _: PayloadAction<OtpRequest['channel']>) => {
      state.loading = true;
    },
    requestVerificationSuccess: (state) => {
      state.loading = false;
      state.verifying = true;
    },
    requestVerificationFail: (state, { payload: { message } }: PayloadAction<ErrorType>) => {
      state.loading = false;
      state.error = { message };
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    verifyOtp: (state, _: PayloadAction<string>) => {
      state.loading = true;
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    authorizeWithOtp: (state, _: PayloadAction<string>) => {
      state.loading = true;
    },
    authorizeWithOtpPhoneInUse: (state) => {
      state.loading = false;
      state.error = null;
    },
    authorizeWithOtpSuccess: (state) => {
      state.loading = false;
      state.authorized = true;
      state.verifying = false;
      state.error = null;
    },
    authorizeWithOtpFail: (state, { payload: { message } }: PayloadAction<ErrorType>) => {
      state.loading = false;
      state.error = { message };
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    authorizeWithOtpConflict: (state, _: PayloadAction<AuthorizationConflictResponse>) => {
      state.loading = false;
      state.error = null;
    },
    authorizationExpiredError: (state, { payload: { message } }: PayloadAction<ErrorType>) => {
      state.loading = false;
      state.error = { message };
    },
    clearAuthorization: (state, action: PayloadAction<'authorized' | 'verifying' | undefined>) => {
      if (action.payload === 'authorized') {
        state.authorized = false;
      } else if (action.payload === 'verifying') {
        state.verifying = false;
      } else {
        state.authorized = false;
        state.verifying = false;
      }

      state.loading = false;
    },
  },
});

export const sessionReducer = slice.reducer;
export const {
  authorizeWithOtp,
  authorizeWithOtpConflict,
  authorizeWithOtpFail,
  authorizationExpiredError,
  authorizeWithOtpPhoneInUse,
  authorizeWithOtpSuccess,
  clearAuthorization,
  loadLocalStorageSession,
  loadLocalStorageSessionSuccess,
  requestOtp,
  requestVerification,
  requestVerificationFail,
  requestVerificationSuccess,
  resendOtp,
  resendVerification,
  verifyOtp,
} = slice.actions;
